import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";

const empty = {
  name: "", phone: "", whatsapp: "", problem: "", service_type: "clinic",
  session_fee: "", payment_type: "per_session", schedule: "daily", custom_days: [1, 2, 3, 4, 5, 6],
  next_visit_date: "", next_visit_time: "", assigned_physio_id: "",
  age: "", gender: "", address: "", locality: "", city: "Ranchi", pincode: "",
  chief_complaint: "", pain_level: "", medical_history: "", previous_treatment: "",
  referred_by: "", source: "walk-in", notes: "", alt_phone: "", google_maps_url: "",
};

export default function PatientFormDialog({ open, onOpenChange, onSaved, editing }) {
  const { user, settings } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [physios, setPhysios] = useState([]);
  const [dups, setDups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/users/physiotherapists").then((r) => setPhysios(r.data)).catch(() => {});
      if (editing) {
        setForm({ ...empty, ...editing, session_fee: editing.session_fee ?? "" });
      } else {
        setForm({ ...empty, session_fee: settings?.default_clinic_fee || 300 });
      }
      setDups([]);
    }
  }, [open, editing, settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkDup = async () => {
    if (editing) return;
    if ((form.phone || "").length < 5 && (form.name || "").length < 3) return;
    try {
      const { data } = await api.get("/patients/check-duplicate", { params: { phone: form.phone, name: form.name } });
      setDups(data.duplicates || []);
    } catch (e) {}
  };

  const onServiceChange = (v) => {
    set("service_type", v);
    if ((v === "home" || v === "both") && !form.session_fee_touched) {
      set("session_fee", settings?.default_home_fee || 500);
    }
  };

  const submit = async () => {
    if (!form.name || !form.phone || !form.problem) {
      toast.error("Name, phone and problem are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        session_fee: Number(form.session_fee) || 0,
        age: form.age ? Number(form.age) : null,
        pain_level: form.pain_level ? Number(form.pain_level) : null,
        assigned_physio_id: form.assigned_physio_id || null,
      };
      let res;
      if (editing) {
        res = await api.put(`/patients/${editing.id}`, payload);
        toast.success("Patient updated");
      } else {
        res = await api.post("/patients", payload);
        toast.success(`Patient registered · ${res.data.patient_id}`);
      }
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Edit Patient" : "Register New Patient"}</DialogTitle>
        </DialogHeader>

        {dups.length > 0 && !editing && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="duplicate-warning">
            <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
              <AlertTriangle className="h-4 w-4" /> Possible existing patient found
            </div>
            <div className="space-y-1">
              {dups.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm bg-white rounded px-2 py-1.5">
                  <span>{d.name} · {d.phone} · <span className="text-slate-400">{d.patient_id}</span></span>
                  <Button size="sm" variant="ghost" className="h-7 text-bk-blue" onClick={() => { onOpenChange(false); navigate(`/patients/${d.id}`); }}>Open</Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Full Name *</Label>
            <Input data-testid="patient-name" value={form.name} onChange={(e) => set("name", e.target.value)} onBlur={checkDup} />
          </div>
          <div className="space-y-1.5">
            <Label>Phone *</Label>
            <Input data-testid="patient-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} onBlur={checkDup} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Problem / Condition *</Label>
            <Input data-testid="patient-problem" value={form.problem} onChange={(e) => set("problem", e.target.value)} placeholder="e.g. Lower back pain" />
          </div>
          <div className="space-y-1.5">
            <Label>Service Type *</Label>
            <Select value={form.service_type} onValueChange={onServiceChange}>
              <SelectTrigger data-testid="patient-service-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="clinic">Clinic</SelectItem>
                <SelectItem value="home">Home Visit</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Session / Visit Charge (₹) *</Label>
            <Input data-testid="patient-fee" type="number" value={form.session_fee} onChange={(e) => { set("session_fee", e.target.value); set("session_fee_touched", true); }} />
          </div>
          <div className="space-y-1.5">
            <Label>Payment Type *</Label>
            <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
              <SelectTrigger data-testid="patient-payment-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="per_session">Per Session</SelectItem>
                <SelectItem value="advance">Advance / Package</SelectItem>
                <SelectItem value="package">Package</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Schedule</Label>
            <Select value={form.schedule} onValueChange={(v) => set("schedule", v)}>
              <SelectTrigger data-testid="patient-schedule"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily (All Days)</SelectItem>
                <SelectItem value="mon_sat">Mon – Sat (6 Days)</SelectItem>
                <SelectItem value="mwf">Mon / Wed / Fri</SelectItem>
                <SelectItem value="tt">Tue / Thu</SelectItem>
                <SelectItem value="custom">Custom Selected Days</SelectItem>
                <SelectItem value="none">No fixed schedule</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.schedule === "custom" && (
            <div className="space-y-1.5 sm:col-span-2 bg-slate-50 p-3 rounded-lg border border-slate-200">
              <Label className="text-xs font-semibold text-slate-700">Select Custom Days:</Label>
              <div className="flex gap-1.5 flex-wrap mt-1">
                {[
                  { id: 1, label: "Mon" },
                  { id: 2, label: "Tue" },
                  { id: 3, label: "Wed" },
                  { id: 4, label: "Thu" },
                  { id: 5, label: "Fri" },
                  { id: 6, label: "Sat" },
                  { id: 0, label: "Sun" },
                ].map((d) => {
                  const selected = (form.custom_days || []).includes(d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => {
                        const cur = form.custom_days || [];
                        const next = selected ? cur.filter((x) => x !== d.id) : [...cur, d.id];
                        set("custom_days", next);
                      }}
                      className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${selected ? "bg-bk-green text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-100"}`}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Next Visit Date (Optional)</Label>
            <Input type="date" value={form.next_visit_date || ""} onChange={(e) => set("next_visit_date", e.target.value)} />
          </div>
          {(form.service_type === "home" || form.service_type === "both") && (
            <div className="space-y-1.5">
              <Label>Assigned Physiotherapist</Label>
              <Select value={form.assigned_physio_id || ""} onValueChange={(v) => set("assigned_physio_id", v)}>
                <SelectTrigger data-testid="patient-physio"><SelectValue placeholder="Select physio" /></SelectTrigger>
                <SelectContent>
                  {physios.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <Collapsible open={showMore} onOpenChange={setShowMore}>
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-1 text-sm text-bk-blue mt-1" data-testid="toggle-optional">
              <ChevronDown className={`h-4 w-4 transition-transform ${showMore ? "rotate-180" : ""}`} /> {showMore ? "Hide" : "Add"} optional details
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="grid sm:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1.5"><Label>WhatsApp</Label><Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Alternate Phone</Label><Input value={form.alt_phone} onChange={(e) => set("alt_phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Age</Label><Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Select value={form.gender || ""} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Locality</Label><Input value={form.locality} onChange={(e) => set("locality", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} /></div>
            {(form.service_type === "home" || form.service_type === "both") && (
              <div className="space-y-1.5 sm:col-span-2"><Label>Google Maps URL</Label><Input value={form.google_maps_url} onChange={(e) => set("google_maps_url", e.target.value)} /></div>
            )}
            <div className="space-y-1.5"><Label>Pain Level (0-10)</Label><Input type="number" min="0" max="10" value={form.pain_level} onChange={(e) => set("pain_level", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Referred By</Label><Input value={form.referred_by} onChange={(e) => set("referred_by", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Medical History</Label><Textarea rows={2} value={form.medical_history} onChange={(e) => set("medical_history", e.target.value)} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </CollapsibleContent>
        </Collapsible>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="patient-save" className="bg-bk-green hover:bg-bk-greenDark">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Register Patient"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
